/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { InputAction } from "../enums/InputAction";
import { EasFlowEvent, EasLogger } from "../logging/EasLogger";
import { Log } from "../logging/Log";
import { DiagnosticSeverity } from "../types";

/**
 * Input policy for a displayed Emergency Alert System (EAS) alert.
 *
 * While an alert is on screen every user input is dropped except the single
 * dismiss action, and that one only when the alert itself permits dismissal
 * (`EasAlert.allowUserExit`, surfaced as `IEamListener.onDisplayEam`'s
 * `allowUserExit`). Nothing else survives: no navigation, no trickplay, no
 * channel change, no numeric entry, no colour buttons, no pointer input, and
 * no app-exit key.
 *
 * ## Why a gate object rather than logic in the interceptor
 *
 * The blocking decision belongs to the EAS feature, but the thing that has to
 * act on it is the navigation pipeline — two different modules that must not
 * depend on each other. `EamModelImpl` toggles this gate; the interceptor
 * built by `createEasInputBlockInterceptor` reads it. Both sides talk to this
 * one object instead of to each other, and neither needs a reference to the
 * `NavigationEngine` instance, which is created in the view layer and is
 * therefore unreachable from the model.
 *
 * A singleton because there is exactly one screen and one alert at a time:
 * the EAS specification never displays two alerts concurrently, and
 * `EamModelImpl` enforces that with {@link EamModelImpl.isAlertActive}.
 *
 * ## Why this is a singleton and not Redux state
 *
 * NAVIGATION_ARCHITECTURE_PROMPT §3 asks for navigation state in Redux and no
 * module-level singletons, and this is a deliberate, narrow exception.
 * `EamModelImpl` owns the alert lifecycle and lives in `src/model/`, which
 * the `model-to-upper` layering rule forbids from importing `src/viewModel/`
 * at all — so it cannot dispatch, and the gate cannot be a slice it writes
 * to. Routing the block through the ViewModel instead would mean the model
 * asking a bridge to set Redux state, the interceptor reading it back, and
 * the dismiss key dispatching a thunk that calls back into the model: three
 * extra hops on the one path that must not be able to fail, to hold a flag
 * that is only ever true while a single modal alert is on screen.
 *
 * `EasResumeState` is a singleton for the same reason and on the same terms.
 */
export class EasInputGate {
  private static readonly TAG: string = "EasInputGate";

  private static mInstance: EasInputGate | null = null;

  /**
   * The one action allowed to pass while an alert is up.
   *
   * `BACK` rather than `NAV_OK` or `EXIT` because that is what the OSD tells
   * the user to press — `EasOsd` renders a fixed "Press BACK to dismiss"
   * footer, shown on exactly the same condition that opens this gate.
   */
  private static readonly DISMISS_ACTION: InputAction = InputAction.BACK;

  /** Whether an alert is currently on screen. */
  private mIsActive: boolean = false;

  /** Whether the active alert may be dismissed by the user. */
  private mIsDismissible: boolean = false;

  /** Invoked when the dismiss key is pressed on a dismissible alert. */
  private mOnDismiss: (() => void) | null = null;

  /**
   * Open the gate for an alert that is going on screen.
   *
   * Idempotent in effect: re-activating for a superseding alert simply
   * re-states the policy — the new alert's `allowUserExit` replaces the old
   * one's.
   *
   * @param isDismissible - Whether this alert allows user dismissal
   * @param onDismiss - Invoked when the dismiss key is pressed. Supplied by
   *   the EAS model rather than imported by the navigation layer, which must
   *   not depend on the push-notification package it is already depended on by.
   */
  public activate(isDismissible: boolean, onDismiss: () => void): void {
    this.mIsActive = true;
    this.mIsDismissible = isDismissible;
    this.mOnDismiss = onDismiss;
    Log.i(
      EasInputGate.TAG,
      `activate(): input blocked, dismissible=${String(isDismissible)}`
    );
  }

  /**
   * Close the gate. All input returns to normal routing.
   *
   * Safe to call when already closed so teardown paths need no guard.
   */
  public deactivate(): void {
    if (!this.mIsActive) {
      return;
    }
    this.mIsActive = false;
    this.mIsDismissible = false;
    this.mOnDismiss = null;
    Log.i(EasInputGate.TAG, "deactivate(): input restored");
  }

  /**
   * Whether `action` may proceed to the rest of the input pipeline.
   *
   * Always true while the gate is closed, so a caller never has to ask
   * whether an alert is up before consulting it.
   *
   * **Nothing passes while an alert is up — the dismiss key included.**
   * Forwarding BACK was the original design and it was wrong: BACK reached
   * the active `ScreenController`, which on the playback screen navigates to
   * the previous screen, so dismissing the alert also tore down playback.
   * The dismiss key is now consumed by the interceptor and turned into
   * {@link requestDismiss} instead, so it never reaches navigation.
   *
   * @returns true when the event may continue, false when it must be dropped
   */
  public isActionAllowed(): boolean {
    return !this.mIsActive;
  }

  /**
   * Whether `action` is the key that dismisses a dismissible alert.
   *
   * False for a non-dismissible alert, so the key is simply swallowed.
   *
   * @param action - The resolved action for the inbound event
   * @returns True when this event should dismiss the alert
   */
  public isDismissRequest(action: InputAction): boolean {
    return (
      this.mIsActive &&
      this.mIsDismissible &&
      action === EasInputGate.DISMISS_ACTION
    );
  }

  /**
   * Ask the owning alert to finish, in response to the dismiss key.
   *
   * A no-op when the alert is not dismissible or no owner registered a
   * callback, so the interceptor never needs to branch.
   */
  public requestDismiss(): void {
    if (!this.mIsActive || !this.mIsDismissible || this.mOnDismiss === null) {
      return;
    }
    Log.i(EasInputGate.TAG, "requestDismiss(): dismiss key pressed");
    EasLogger.logFlowEvent(
      EasFlowEvent.DismissRequested,
      "EAS dismiss key accepted by the gate",
      DiagnosticSeverity.Info
    );
    this.mOnDismiss();
  }

  /**
   * Get the shared instance, creating it on first use.
   *
   * @returns The singleton instance
   */
  public static getInstance(): EasInputGate {
    if (EasInputGate.mInstance === null) {
      EasInputGate.mInstance = new EasInputGate();
    }
    return EasInputGate.mInstance;
  }

  /**
   * Discard the shared instance so the next getInstance() builds a fresh one.
   * Test-only.
   */
  public static __resetForTest(): void {
    EasInputGate.mInstance = null;
  }
}
